import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../lib/api';
import { SophiaAvatar } from '../components/SophiaAvatar';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { ArrowLeft, User, PawPrint, Image, Download, Loader2, Share2, X, RefreshCw, ThumbsUp } from 'lucide-react';

const SCENES = [
  { key: 'elegant', label: { en: 'Elegant Setting', de: 'Elegantes Setting' } },
  { key: 'livingRoom', label: { en: 'Living Room', de: 'Wohnzimmer' } },
  { key: 'redCarpet', label: { en: 'Red Carpet', de: 'Roter Teppich' } },
  { key: 'fancyBar', label: { en: 'Fancy Bar', de: 'Schicke Bar' } },
  { key: 'flamingo', label: { en: 'Flamingo Beach', de: 'Flamingo-Strand' } },
  { key: 'tiffanies', label: { en: "At Tiffany's", de: 'Bei Tiffany' } },
  { key: 'podcast', label: { en: 'Video Podcast', de: 'Video Podcast' } },
  { key: 'goldenFrame', label: { en: 'Golden Frame Portrait', de: 'Goldrahmen-Porträt' } },
  { key: 'garden', label: { en: 'Garden', de: 'Garten' } },
  { key: 'other', label: { en: 'Other...', de: 'Anderes...' } },
];

const SCENE_VALUES = {
  elegant: 'an opulent elegant setting with chandeliers, gold accents, and luxurious velvet furnishings',
  livingRoom: 'a glamorous pink and gold living room with velvet sofas, fresh flowers, and warm lighting',
  redCarpet: 'a glamorous Hollywood red carpet premiere at night with a lit-up HOLLYWOOD marquee sign, palm trees wrapped in fairy lights, paparazzi with cameras, velvet ropes, and PINK FLAMINGOS walking on the red carpet as elegant companions',
  fancyBar: 'a glamorous art deco cocktail bar with pink neon lights, marble counters, and champagne',
  flamingo: 'a dreamy tropical paradise with pink flamingos, turquoise pools, and lush palm trees',
  tiffanies: "the iconic Tiffany & Co. store front with the famous blue color, diamonds, and elegance",
  podcast: 'a professional podcast studio setup - the two people are SITTING ACROSS FROM EACH OTHER at a table (NOT facing the camera), both wearing over-ear HEADPHONES, speaking into professional microphones on boom arms, with a floral wallpaper background in pink/coral tones, warm lamp lighting, coffee mugs on the table, and an ON AIR illuminated sign visible in the background',
  goldenFrame: 'a formal portrait inside an ORNATE GOLDEN BAROQUE FRAME with elaborate carved scroll details and gilded flourishes, set against a soft-focus background of large pink and coral PEONY FLOWERS and roses, styled like a classic aristocratic oil painting masterpiece',
  garden: 'a beautiful English rose garden with blooming pink flowers, stone pathways, and fountains',
};

const INSTAGRAM_URL = 'https://www.instagram.com/sophia_einstein_the_wise/';

// Sophia's sassy approval messages
const SOPHIA_APPROVAL_MESSAGES = {
  en: [
    "Is this fabulous enough, darling?",
    "Well, what do you think? Too gorgeous or just right?",
    "Darling, does this capture your essence?",
    "Is this masterpiece worthy of your wall?",
  ],
  de: [
    "Ist das fabelhaft genug, Liebling?",
    "Nun, was denkst du? Zu prächtig oder genau richtig?",
    "Liebling, fängt das deine Essenz ein?",
    "Ist dieses Meisterwerk deiner Wand würdig?",
  ],
};

export default function PhotoPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const personInputRef = useRef(null);
  const petInputRef = useRef(null);
  
  // Separate state for person and pet images
  const [personImage, setPersonImage] = useState(null);
  const [personPreview, setPersonPreview] = useState(null);
  const [petImage, setPetImage] = useState(null);
  const [petPreview, setPetPreview] = useState(null);
  
  const [scene, setScene] = useState('elegant');
  const [customScene, setCustomScene] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showApproval, setShowApproval] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [isApproved, setIsApproved] = useState(false);

  const handlePersonSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPersonImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPersonPreview(ev.target.result);
      reader.readAsDataURL(file);
      setGeneratedImage(null);
      setError(null);
      setShowApproval(false);
      setIsApproved(false);
    }
  };

  const handlePetSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPetImage(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPetPreview(ev.target.result);
      reader.readAsDataURL(file);
      setGeneratedImage(null);
      setError(null);
      setShowApproval(false);
      setIsApproved(false);
    }
  };

  const clearPerson = () => {
    setPersonImage(null);
    setPersonPreview(null);
    if (personInputRef.current) personInputRef.current.value = '';
  };

  const clearPet = () => {
    setPetImage(null);
    setPetPreview(null);
    if (petInputRef.current) petInputRef.current.value = '';
  };

  const getRandomApprovalMessage = () => {
    const messages = SOPHIA_APPROVAL_MESSAGES[language] || SOPHIA_APPROVAL_MESSAGES.en;
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const generatePhoto = async () => {
    if (!personImage && !petImage) return;
    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);
    setShowApproval(false);
    setIsApproved(false);

    try {
      const formData = new FormData();
      
      if (personImage && petImage) {
        formData.append('image', personImage);
        formData.append('subject_type', 'both');
      } else if (petImage) {
        formData.append('image', petImage);
        formData.append('subject_type', 'pet');
      } else {
        formData.append('image', personImage);
        formData.append('subject_type', 'person');
      }
      
      const sceneValue = scene === 'other' ? customScene : (SCENE_VALUES[scene] || scene);
      formData.append('scene', sceneValue);
      formData.append('language', language);

      const { data } = await api.generatePhoto(formData);
      setGeneratedImage(`data:${data.mime_type || 'image/png'};base64,${data.image_base64}`);
      
      // Show Sophia's approval question
      setApprovalMessage(getRandomApprovalMessage());
      setShowApproval(true);
    } catch (err) {
      console.error('Photo generation error:', err);
      const msg = err.response?.data?.detail || '';
      if (err.response?.status === 429) {
        setError(msg);
      } else {
        setError(language === 'de' ? 'Fehler beim Erstellen. Bitte versuche es erneut.' : 'Generation failed. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = () => {
    setShowApproval(false);
    setIsApproved(true);
  };

  const handleRegenerate = () => {
    setShowApproval(false);
    setIsApproved(false);
    generatePhoto();
  };

  // Download image to device
  const downloadImage = useCallback(() => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = 'sophia-einstein-photo.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [generatedImage]);

  // Share image using native share API with file
  const shareImageWithFile = useCallback(async () => {
    if (!generatedImage) return;
    
    try {
      // Convert data URL to blob
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const file = new File([blob], 'sophia-einstein-photo.png', { type: 'image/png' });
      
      // Check if we can share files
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Photo with Sophia Einstein',
          text: 'Check out my photo with Sophia Einstein! @sophia_einstein_the_wise',
        });
      } else if (navigator.share) {
        // Fallback to sharing without file
        await navigator.share({
          title: 'Photo with Sophia Einstein',
          text: 'Check out my photo with Sophia Einstein! @sophia_einstein_the_wise',
          url: window.location.href,
        });
      } else {
        // No share API, just download
        downloadImage();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.log('Share failed, falling back to download');
        downloadImage();
      }
    }
  }, [generatedImage, downloadImage]);

  const hasAnyImage = personImage || petImage;
  const canGenerate = hasAnyImage && (scene !== 'other' || customScene.trim());

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <motion.header 
        className="flex items-center justify-between px-6 py-4 border-b border-border/50 bg-background/90 sticky top-0 z-30" 
        initial={{ y: -20, opacity: 0 }} 
        animate={{ y: 0, opacity: 1 }}
      >
        <Button data-testid="photo-back-btn" variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-display text-xl font-semibold">
          {language === 'de' ? 'Foto mit Sophia' : 'Photo with Sophia'}
        </h1>
        <LanguageSelector />
      </motion.header>

      <div className="flex-1 px-6 py-6 max-w-lg mx-auto w-full space-y-5 pb-24">
        {/* Sophia avatar */}
        <motion.div className="flex flex-col items-center gap-2" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
          <SophiaAvatar variant="teal" size="lg" />
        </motion.div>

        {/* Two separate upload areas */}
        <motion.div 
          className="grid grid-cols-2 gap-4" 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2 }}
        >
          {/* Person Upload */}
          <div className="space-y-2">
            <label className="font-body text-xs text-muted-foreground flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" />
              {language === 'de' ? 'Dein Foto' : 'Your Photo'}
            </label>
            <input ref={personInputRef} type="file" accept="image/*" onChange={handlePersonSelect} className="hidden" />
            <motion.button
              data-testid="photo-upload-person-btn"
              onClick={() => personInputRef.current?.click()}
              className="w-full aspect-square rounded-2xl border-2 border-dashed border-sophia-pink-500/30 hover:border-sophia-pink-500/60 bg-sophia-pink-500/5 flex flex-col items-center justify-center gap-2 transition-all overflow-hidden relative"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {personPreview ? (
                <>
                  <img src={personPreview} alt="Person" className="w-full h-full object-cover rounded-2xl" />
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); clearPerson(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clearPerson(); }}}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </div>
                </>
              ) : (
                <>
                  <User className="w-8 h-8 text-sophia-pink-500/50" />
                  <p className="font-body text-[10px] text-sophia-pink-500/70 text-center px-2">
                    {language === 'de' ? 'Person hochladen' : 'Upload Person'}
                  </p>
                </>
              )}
            </motion.button>
          </div>

          {/* Pet Upload */}
          <div className="space-y-2">
            <label className="font-body text-xs text-muted-foreground flex items-center gap-1.5">
              <PawPrint className="w-3.5 h-3.5" />
              {language === 'de' ? 'Dein Haustier' : 'Your Pet'}
            </label>
            <input ref={petInputRef} type="file" accept="image/*" onChange={handlePetSelect} className="hidden" />
            <motion.button
              data-testid="photo-upload-pet-btn"
              onClick={() => petInputRef.current?.click()}
              className="w-full aspect-square rounded-2xl border-2 border-dashed border-mystic-teal-500/30 hover:border-mystic-teal-500/60 bg-mystic-teal-500/5 flex flex-col items-center justify-center gap-2 transition-all overflow-hidden relative"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {petPreview ? (
                <>
                  <img src={petPreview} alt="Pet" className="w-full h-full object-cover rounded-2xl" />
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); clearPet(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clearPet(); }}}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </div>
                </>
              ) : (
                <>
                  <PawPrint className="w-8 h-8 text-mystic-teal-500/50" />
                  <p className="font-body text-[10px] text-mystic-teal-500/70 text-center px-2">
                    {language === 'de' ? 'Haustier hochladen' : 'Upload Pet'}
                  </p>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Selection indicator */}
        {hasAnyImage && (
          <motion.p 
            className="text-center text-xs font-body text-sophia-pink-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {personImage && petImage 
              ? (language === 'de' ? 'Person & Haustier werden mit Sophia fotografiert' : 'Person & Pet will be photographed with Sophia')
              : personImage 
                ? (language === 'de' ? 'Person wird mit Sophia fotografiert' : 'Person will be photographed with Sophia')
                : (language === 'de' ? 'Haustier wird mit Sophia fotografiert' : 'Pet will be photographed with Sophia')
            }
          </motion.p>
        )}

        {/* Scene selection */}
        <motion.div className="space-y-2" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
          <label className="font-body text-sm text-muted-foreground">
            {language === 'de' ? 'Wähle eine Szene' : 'Choose a scene'}
          </label>
          <div className="grid grid-cols-3 gap-2">
            {SCENES.map(({ key, label }) => (
              <button
                key={key}
                data-testid={`photo-scene-${key}-btn`}
                onClick={() => setScene(key)}
                className={`h-10 rounded-xl flex items-center justify-center border transition-all font-body text-[11px] px-2 ${
                  scene === key
                    ? 'bg-sophia-pink-500/15 border-sophia-pink-500 text-foreground font-medium'
                    : 'border-border text-muted-foreground hover:border-sophia-pink-500/30'
                }`}
              >
                {label[language] || label.en}
              </button>
            ))}
          </div>

          <AnimatePresence>
            {scene === 'other' && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                <input
                  data-testid="photo-custom-scene-input"
                  type="text"
                  value={customScene}
                  onChange={(e) => setCustomScene(e.target.value)}
                  placeholder={language === 'de' ? 'Beschreibe deine Traumszene...' : 'Describe your dream scene...'}
                  className="w-full h-11 px-4 mt-2 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body text-sm"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Generate */}
        <Button
          data-testid="photo-generate-btn"
          onClick={generatePhoto}
          disabled={!canGenerate || isGenerating}
          className="w-full h-14 rounded-full bg-sophia-pink-500 hover:bg-sophia-pink-700 text-white font-display text-lg shadow-lg disabled:opacity-40"
        >
          {isGenerating ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              {language === 'de' ? 'Wird erstellt...' : 'Creating...'}
            </div>
          ) : (
            <>
              <Image className="w-5 h-5 mr-2" />
              {language === 'de' ? 'Foto erstellen' : 'Create Photo'}
            </>
          )}
        </Button>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.p data-testid="photo-error" className="text-center text-sm text-red-500 font-body" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Generated image result - NEW LAYOUT */}
        <AnimatePresence>
          {generatedImage && (
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            >
              {/* 1. Generated Image - Full size, sharp, no blur */}
              <div className="bg-card rounded-3xl border border-border/50 shadow-xl p-3 overflow-hidden">
                <img 
                  data-testid="photo-generated-image" 
                  src={generatedImage} 
                  alt="Photo with Sophia" 
                  className="w-full rounded-2xl"
                />
                
                {/* Branded footer - Instagram link */}
                <div className="mt-3 flex items-center justify-between px-2 pb-1">
                  <a
                    data-testid="photo-instagram-link"
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <SophiaAvatar variant="pink" size="sm" animate={false} />
                    <div>
                      <p className="font-display text-sm font-semibold text-foreground">Sophia Einstein</p>
                      <p className="font-display text-[11px] text-sophia-pink-500 italic">@sophia_einstein_the_wise</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* 2. Sophia's Approval Dialog - BELOW the image */}
              <AnimatePresence>
                {showApproval && (
                  <motion.div
                    className="bg-card rounded-2xl p-5 border border-sophia-pink-500/30 shadow-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <SophiaAvatar variant="pink" size="sm" animate={false} />
                      <div>
                        <p className="font-display text-sm font-semibold">Sophia</p>
                        <p className="font-body text-xs text-muted-foreground">
                          {language === 'de' ? 'fragt dich...' : 'asks you...'}
                        </p>
                      </div>
                    </div>
                    
                    {/* 3. Approval message */}
                    <p className="font-display text-lg text-foreground mb-5 leading-relaxed">
                      {approvalMessage}
                    </p>
                    
                    {/* 4. Try Again | Perfect! buttons */}
                    <div className="flex gap-3">
                      <Button
                        data-testid="photo-regenerate-btn"
                        onClick={handleRegenerate}
                        variant="outline"
                        className="flex-1 h-12 rounded-full border-sophia-pink-500/30 text-sophia-pink-500 hover:bg-sophia-pink-500/10 font-body gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {language === 'de' ? 'Nochmal' : 'Try Again'}
                      </Button>
                      <Button
                        data-testid="photo-approve-btn"
                        onClick={handleApprove}
                        className="flex-1 h-12 rounded-full bg-sophia-pink-500 hover:bg-sophia-pink-600 text-white font-body gap-2"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        {language === 'de' ? 'Perfekt!' : 'Perfect!'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 5. Download + Share buttons - After approval */}
              <AnimatePresence>
                {isApproved && (
                  <motion.div
                    className="space-y-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="flex gap-3">
                      <Button 
                        data-testid="photo-download-btn" 
                        onClick={downloadImage} 
                        variant="outline"
                        className="flex-1 h-12 rounded-full border-mystic-teal-500/50 text-mystic-teal-500 hover:bg-mystic-teal-500/10 font-body gap-2"
                      >
                        <Download className="w-5 h-5" />
                        {language === 'de' ? 'Speichern' : 'Save Image'}
                      </Button>
                      <Button 
                        data-testid="photo-share-btn" 
                        onClick={shareImageWithFile} 
                        className="flex-1 h-12 rounded-full bg-sophia-pink-500 hover:bg-sophia-pink-600 text-white font-body gap-2"
                      >
                        <Share2 className="w-5 h-5" />
                        {language === 'de' ? 'Teilen' : 'Share'}
                      </Button>
                    </div>
                    
                    {/* Success message */}
                    <p className="text-center text-sm text-muted-foreground font-body">
                      {language === 'de' 
                        ? 'Dein Meisterwerk ist bereit! Teile es auf Instagram oder WhatsApp.' 
                        : 'Your masterpiece is ready! Share it on Instagram or WhatsApp.'}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
